import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSupabaseClient, isSupabaseConfigured } from '../services/supabaseClient';
import { SearchInput } from '../components/SearchInput';
import { PeptideCard, VendorProduct } from '../components/PeptideCard';
import { DownloadButtons } from '../components/DownloadButtons';
import { SEO } from '../components/SEO';
import { getDiscountFromCoupon } from '../utils/discountUtils';
import { selectBestCoupon } from '../utils/coupons';
import { trackVendorClick } from '../services/vendorClickTrackingService';
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateProductSchema,
  getBaseUrl,
} from '../utils/seo';
import './HomePage.css';

const PAGE_SIZE = 25;
const DEBOUNCE_DELAY = 400;
// Batch size for fetching products when client-side sorting is needed
// This prevents fetching all products at once which can hit Supabase limits
const CLIENT_SORT_BATCH_SIZE = 1000;

export type SortOption =
  | 'canonical_peptide_asc'
  | 'price_asc'
  | 'price_per_mg_asc'
  | 'price_per_mg_desc'
  | 'vendor_name_asc';

interface SortOptionConfig {
  value: SortOption;
  label: string;
}

const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'price_per_mg_asc', label: 'Price per mg (Low → High)' },
  { value: 'price_per_mg_desc', label: 'Price per mg (High → Low)' },
  { value: 'price_asc', label: 'Total price (Low → High)' },
  { value: 'vendor_name_asc', label: 'Vendor (A–Z)' },
];

interface PeptideSuggestion {
  name: string;
}

/**
 * SEO-optimized Home Page with peptide search
 */
export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [sortOption, setSortOption] = useState<SortOption>('price_per_mg_asc');
  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allPeptideNames, setAllPeptideNames] = useState<PeptideSuggestion[]>([]);
  const [topPeptides, setTopPeptides] = useState<string[]>([]);
  // Store all sorted products when sorting by price_per_mg (for client-side pagination)
  const [allSortedProducts, setAllSortedProducts] = useState<VendorProduct[]>([]);
  // Use ref to access latest allSortedProducts in callbacks
  const allSortedProductsRef = useRef<VendorProduct[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    allSortedProductsRef.current = allSortedProducts;
  }, [allSortedProducts]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      // Update URL
      if (searchQuery.trim()) {
        setSearchParams({ q: searchQuery.trim() });
      } else {
        setSearchParams({});
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery, setSearchParams]);

  // Reset page when search or sort changes
  useEffect(() => {
    setPage(0);
    setProducts([]);
    setAllSortedProducts([]); // Clear cached sorted products
  }, [debouncedQuery, sortOption]);

  // Fetch all unique canonical peptide names for autocomplete
  const fetchPeptideNames = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      return;
    }

    try {
      const suggestions: PeptideSuggestion[] = [];
      const nameSet = new Set<string>();

      // Helper to add a name if it looks valid
      const addName = (raw?: string | null) => {
        const candidate = raw?.trim();
        if (!candidate) return;
        if (candidate.length < 3) return; // skip very short strings
        if (/^\d+$/.test(candidate)) return; // skip pure numbers
        if (nameSet.has(candidate)) return;
        nameSet.add(candidate);
        suggestions.push({ name: candidate });
      };
      let from = 0;
      const pageSize = 1000; // Supabase max per request
      let hasMore = true;

      // Fetch all rows using pagination to get all peptide names
      // Primary source: canonical_peptide
      // Fallbacks: peptide_name_raw and product_name (first/major tokens) to surface single-peptide names present in listings
      // Continue fetching until we get fewer rows than pageSize (indicating we've reached the end)
      while (hasMore) {
        const supabase = getSupabaseClient();
        if (!supabase) {
          break;
        }
        const { data, error } = await supabase
          .from('vendor_peptide_offers')
          .select('canonical_peptide, peptide_name_raw, product_name')
          .order('canonical_peptide', { ascending: true, nullsFirst: true }) // deterministic pagination
          .range(from, from + pageSize - 1);

        if (error) throw error;

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        data.forEach((row: any) => {
          const canonicalName = row.canonical_peptide;
          const rawName = row.peptide_name_raw;
          const productName = row.product_name;

          // Add canonical name (full and split parts)
          addName(canonicalName);
          const canonicalParts = canonicalName
            ?.split(/,|\+| and /i)
            .map((p: string) => p.trim())
            .filter(Boolean) ?? [];
          canonicalParts.forEach((part: string) => addName(part));

          // Add peptide_name_raw (full and split parts)
          addName(rawName);
          const rawParts = rawName
            ?.split(/,|\+| and /i)
            .map((p: string) => p.trim())
            .filter(Boolean) ?? [];
          rawParts.forEach((part: string) => addName(part));

          // Add from product_name: first token and split parts to catch single peptides in product titles
          if (productName) {
            // First token (e.g., "Tirzepatide" from "Tirzepatide 10mg")
            const firstTokenMatch = productName.match(/^[A-Za-z][A-Za-z0-9-]*/);
            if (firstTokenMatch) {
              addName(firstTokenMatch[0]);
            }

            // Split on common separators to capture blends
            const productParts = productName
              .split(/,|\+| and /i)
              .map((p: string) => p.trim())
              .filter(Boolean);
            productParts.forEach((part: string) => {
              // Also take first token of each part to avoid dosage suffixes
              const token = part.match(/^[A-Za-z][A-Za-z0-9-]*/)?.[0] ?? part;
              addName(token);
            });
          }
        });

        // Check if we got a full page (might be more data)
        // If we got exactly pageSize rows, there might be more pages to fetch
        hasMore = data.length === pageSize;
        from += pageSize;
        
        // Safety check: prevent infinite loops (max 200 pages = 200,000 rows)
        if (from > 200000) {
          console.warn('Reached maximum pagination limit for peptide names');
          hasMore = false;
          break;
        }
      }

      suggestions.sort((a, b) => a.name.localeCompare(b.name));
      setAllPeptideNames(suggestions);
    } catch (err) {
      console.error('Error fetching peptide names:', err);
    }
  }, []);

  useEffect(() => {
    fetchPeptideNames();
  }, [fetchPeptideNames]);

  // Fetch top peptides based on frequency in database
  const fetchTopPeptides = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      return;
    }

    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        return;
      }

      // Fetch a sample of products to analyze
      const { data, error } = await supabase
        .from('vendor_peptide_offers')
        .select('canonical_peptide, peptide_name_raw')
        .limit(5000); // Sample size to get good distribution

      if (error) throw error;

      // Count occurrences of each peptide
      const peptideCounts = new Map<string, number>();

      (data || []).forEach((row: any) => {
        // Prefer canonical_peptide, fallback to peptide_name_raw
        const peptideName = row.canonical_peptide || row.peptide_name_raw;
        
        if (peptideName && peptideName.trim()) {
          // Extract first peptide from blends (split by comma, +, or "and")
          const firstPeptide = peptideName
            .split(/,|\+| and /i)[0]
            .trim()
            .split(/\s+/)[0]; // Get first word to avoid dosage suffixes
          
          if (firstPeptide && firstPeptide.length >= 3) {
            const count = peptideCounts.get(firstPeptide) || 0;
            peptideCounts.set(firstPeptide, count + 1);
          }
        }
      });

      // Sort by count and take top 10
      const sortedPeptides = Array.from(peptideCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name]) => name)
        .filter((name) => name && name.length >= 3);

      setTopPeptides(sortedPeptides);
    } catch (err) {
      console.error('Error fetching top peptides:', err);
    }
  }, []);

  useEffect(() => {
    fetchTopPeptides();
  }, [fetchTopPeptides]);

  // Filter autocomplete suggestions
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }
    const query = searchQuery.toLowerCase();
    // Return all matching suggestions - no limit
    return allPeptideNames
      .filter((suggestion) => suggestion.name.toLowerCase().includes(query));
  }, [searchQuery, allPeptideNames]);

  // Get sort configuration
  interface SortConfig {
    column: string;
    ascending: boolean;
    nullsFirst?: boolean;
  }

  const getSortConfig = useCallback((sort: SortOption): SortConfig[] => {
    switch (sort) {
      case 'canonical_peptide_asc':
        return [
          { column: 'canonical_peptide', ascending: true, nullsFirst: false },
          { column: 'price_per_mg', ascending: true, nullsFirst: false },
        ];
      case 'price_asc':
        return [{ column: 'price', ascending: true }];
      case 'price_per_mg_asc':
        return [{ column: 'price_per_mg', ascending: true, nullsFirst: false }];
      case 'price_per_mg_desc':
        return [{ column: 'price_per_mg', ascending: false, nullsFirst: false }];
      case 'vendor_name_asc':
        return [{ column: 'product_name', ascending: true }];
      default:
        return [{ column: 'price_per_mg', ascending: true, nullsFirst: false }];
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async (query: string, pageNum: number, sort: SortOption) => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
      setLoading(false);
      setIsLoadingMore(false);
      return;
    }

    try {
      // For price_per_mg, vendor_name, and price sorting, we need to fetch all products and sort client-side
      // - price_per_mg: because discounts are calculated client-side
      // - vendor_name_asc: because we sort by vendor_name but database sorts by product_name
      // - price_asc: to ensure stable sorting across pagination
      const needsClientSideSort = sort === 'price_per_mg_asc' || sort === 'price_per_mg_desc' || 
                                   sort === 'vendor_name_asc' || sort === 'price_asc';

      if (needsClientSideSort && pageNum === 0) {
        // First page: fetch products in batches to avoid hitting Supabase limits
        setLoading(true);
        setError(null);

        // Fetch products in batches, accumulating them
        let allProducts: VendorProduct[] = [];
        let batchStart = 0;
        let hasMoreBatches = true;
        const maxBatches = 3; // Fetch up to 3 batches initially (3000 products max)

        while (hasMoreBatches && batchStart < maxBatches) {
          const supabase = getSupabaseClient();
          if (!supabase) {
            break;
          }
          const batchEnd = batchStart * CLIENT_SORT_BATCH_SIZE + CLIENT_SORT_BATCH_SIZE - 1;
          
          let supabaseQuery = supabase
            .from('vendor_peptide_offers')
            .select('*, vendors(name, logo_url, coupon_code, coupon_description, website_url, scraped_coupon_code, scraped_coupon_discount, scraped_coupon_expires_at, scraped_coupon_last_seen, scraped_coupon_source)')
            .range(batchStart * CLIENT_SORT_BATCH_SIZE, batchEnd);

          if (query.trim()) {
            const searchPattern = `%${query.trim()}%`;
            supabaseQuery = supabaseQuery.ilike('canonical_peptide', searchPattern);
          }

          // Order by appropriate column as a rough sort (will be re-sorted client-side)
          if (sort === 'price_per_mg_asc' || sort === 'price_per_mg_desc') {
            supabaseQuery = supabaseQuery.order('price_per_mg', {
              ascending: sort === 'price_per_mg_asc',
              nullsFirst: false,
            });
          } else if (sort === 'price_asc') {
            supabaseQuery = supabaseQuery.order('price', {
              ascending: true,
            });
          } else if (sort === 'vendor_name_asc') {
            supabaseQuery = supabaseQuery.order('product_name', {
              ascending: true,
            });
          }

          const { data, error: queryError } = await supabaseQuery;

          if (queryError) throw queryError;

          if (!data || data.length === 0) {
            hasMoreBatches = false;
            break;
          }

          const batchProducts: VendorProduct[] = (data || []).map((row: any) => ({
          id: row.id,
          vendor_id: row.vendor_id,
          vendor_name: row.vendors?.name || 'Unknown Vendor',
          canonical_peptide: row.canonical_peptide,
          peptide_name_raw: row.peptide_name_raw,
          product_name: row.product_name,
          product_url: row.product_url,
          price: Number(row.price),
          currency: row.currency || 'USD',
          vial_size_text: row.vial_size_text,
          vial_size_mg: row.vial_size_mg ? Number(row.vial_size_mg) : null,
          price_per_mg: row.price_per_mg ? Number(row.price_per_mg) : null,
          coupon_code: row.vendors?.coupon_code || null,
          coupon_description: row.vendors?.coupon_description || null,
          scraped_coupon_code: row.vendors?.scraped_coupon_code || null,
          scraped_coupon_discount: row.vendors?.scraped_coupon_discount || null,
          vendor_website_url: row.vendors?.website_url || null,
          vendor_logo_url: row.vendors?.logo_url || null,
          affiliate_url: row.affiliate_url || null,
          }));

          allProducts = [...allProducts, ...batchProducts];

          // If we got fewer products than the batch size, we've reached the end
          if (data.length < CLIENT_SORT_BATCH_SIZE) {
            hasMoreBatches = false;
            break;
          }

          batchStart++;
        }

        // Calculate effective price_per_mg for price_per_mg sorts (discounts are client-side)
        if (sort === 'price_per_mg_asc' || sort === 'price_per_mg_desc') {
          allProducts.forEach((product) => {
            const activeCoupon = selectBestCoupon({
              coupon_code: product.coupon_code,
              coupon_description: product.coupon_description,
              scraped_coupon_code: product.scraped_coupon_code,
              scraped_coupon_discount: product.scraped_coupon_discount,
            });

            const discount = getDiscountFromCoupon(
              product.price,
              product.vial_size_mg,
              activeCoupon.description
            );

            (product as any).effectivePricePerMg = discount?.discountedPricePerMg ?? product.price_per_mg;
          });
        }

        // Sort based on sort option
        allProducts.sort((a, b) => {
          if (sort === 'price_per_mg_asc' || sort === 'price_per_mg_desc') {
            // Sort by effective price_per_mg
            const aPrice = (a as any).effectivePricePerMg;
            const bPrice = (b as any).effectivePricePerMg;

            // Handle null values - put them at the end
            if (aPrice === null && bPrice === null) return 0;
            if (aPrice === null) return 1;
            if (bPrice === null) return -1;

            if (sort === 'price_per_mg_asc') {
              return aPrice - bPrice;
            } else {
              return bPrice - aPrice;
            }
          } else if (sort === 'vendor_name_asc') {
            // Sort by vendor name
            return a.vendor_name.localeCompare(b.vendor_name);
          } else if (sort === 'price_asc') {
            // Sort by price numerically
            return a.price - b.price;
          } else {
            // Fallback: should not reach here for client-side sorts, but handle gracefully
            console.warn(`Unexpected sort option in client-side sort: ${sort}`);
            return 0;
          }
        });

        // Store all sorted products for pagination
        setAllSortedProducts(allProducts);
        allSortedProductsRef.current = allProducts;

        // Get first page
        const firstPage = allProducts.slice(0, PAGE_SIZE);
        setProducts(firstPage);
        setHasMore(allProducts.length > PAGE_SIZE);
      } else if (needsClientSideSort && pageNum > 0) {
        // Subsequent pages: paginate from cached sorted products
        setIsLoadingMore(true);
        
        // Use ref to get latest value
        const cachedProducts = allSortedProductsRef.current;
        
        // If cached products is empty, something went wrong - refetch
        if (cachedProducts.length === 0) {
          console.warn('allSortedProducts is empty, refetching products in batches');
          // Refetch products in batches
          let allProducts: VendorProduct[] = [];
          let batchStart = 0;
          let hasMoreBatches = true;
          const maxBatches = 3; // Fetch up to 3 batches initially

          while (hasMoreBatches && batchStart < maxBatches) {
            const supabase = getSupabaseClient();
            if (!supabase) {
              break;
            }
            const batchEnd = batchStart * CLIENT_SORT_BATCH_SIZE + CLIENT_SORT_BATCH_SIZE - 1;
            
            let refetchQuery = supabase
              .from('vendor_peptide_offers')
              .select('*, vendors(name, logo_url, coupon_code, coupon_description, website_url, scraped_coupon_code, scraped_coupon_discount, scraped_coupon_expires_at, scraped_coupon_last_seen, scraped_coupon_source)')
              .range(batchStart * CLIENT_SORT_BATCH_SIZE, batchEnd);

            if (query.trim()) {
              const searchPattern = `%${query.trim()}%`;
              refetchQuery = refetchQuery.or(
                `canonical_peptide.ilike.${searchPattern},peptide_name_raw.ilike.${searchPattern},product_name.ilike.${searchPattern}`
              );
            }

            // Order by appropriate column
            if (sort === 'price_per_mg_asc' || sort === 'price_per_mg_desc') {
              refetchQuery = refetchQuery.order('price_per_mg', {
                ascending: sort === 'price_per_mg_asc',
                nullsFirst: false,
              });
            } else if (sort === 'price_asc') {
              refetchQuery = refetchQuery.order('price', {
                ascending: true,
              });
            } else if (sort === 'vendor_name_asc') {
              refetchQuery = refetchQuery.order('product_name', {
                ascending: true,
              });
            }

            const { data, error: refetchError } = await refetchQuery;
            if (refetchError) throw refetchError;

            if (!data || data.length === 0) {
              hasMoreBatches = false;
              break;
            }

            const batchProducts: VendorProduct[] = (data || []).map((row: any) => ({
            id: row.id,
            vendor_id: row.vendor_id,
            vendor_name: row.vendors?.name || 'Unknown Vendor',
            canonical_peptide: row.canonical_peptide,
            peptide_name_raw: row.peptide_name_raw,
            product_name: row.product_name,
            product_url: row.product_url,
            price: Number(row.price),
            currency: row.currency || 'USD',
            vial_size_text: row.vial_size_text,
            vial_size_mg: row.vial_size_mg ? Number(row.vial_size_mg) : null,
            price_per_mg: row.price_per_mg ? Number(row.price_per_mg) : null,
            coupon_code: row.vendors?.coupon_code || null,
            coupon_description: row.vendors?.coupon_description || null,
            scraped_coupon_code: row.vendors?.scraped_coupon_code || null,
            scraped_coupon_discount: row.vendors?.scraped_coupon_discount || null,
            vendor_website_url: row.vendors?.website_url || null,
            vendor_logo_url: row.vendors?.logo_url || null,
            affiliate_url: row.affiliate_url || null,
            }));

            allProducts = [...allProducts, ...batchProducts];

            if (data.length < CLIENT_SORT_BATCH_SIZE) {
              hasMoreBatches = false;
              break;
            }

            batchStart++;
          }

          allProducts.forEach((product) => {
            const activeCoupon = selectBestCoupon({
              coupon_code: product.coupon_code,
              coupon_description: product.coupon_description,
              scraped_coupon_code: product.scraped_coupon_code,
              scraped_coupon_discount: product.scraped_coupon_discount,
            });

            const discount = getDiscountFromCoupon(
              product.price,
              product.vial_size_mg,
              activeCoupon.description
            );

            (product as any).effectivePricePerMg = discount?.discountedPricePerMg ?? product.price_per_mg;
          });

          allProducts.sort((a, b) => {
            const aPrice = (a as any).effectivePricePerMg;
            const bPrice = (b as any).effectivePricePerMg;

            if (aPrice === null && bPrice === null) return 0;
            if (aPrice === null) return 1;
            if (bPrice === null) return -1;

            if (sort === 'price_per_mg_asc') {
              return aPrice - bPrice;
            } else {
              return bPrice - aPrice;
            }
          });

          setAllSortedProducts(allProducts);
          allSortedProductsRef.current = allProducts;
          
          const start = pageNum * PAGE_SIZE;
          const end = start + PAGE_SIZE;
          const nextPage = allProducts.slice(start, end);
          setProducts((prev) => [...prev, ...nextPage]);
          setHasMore(end < allProducts.length);
        } else {
          // Normal case: paginate from cached sorted products
          const start = pageNum * PAGE_SIZE;
          const end = start + PAGE_SIZE;
          const nextPage = cachedProducts.slice(start, end);
          setProducts((prev) => [...prev, ...nextPage]);
          setHasMore(end < cachedProducts.length);
        }
        
        setIsLoadingMore(false);
      } else {
        // For other sorts, use database pagination
        if (pageNum === 0) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const supabase = getSupabaseClient();
        if (!supabase) {
          setError('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
          setLoading(false);
          setIsLoadingMore(false);
          return;
        }

        const start = pageNum * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        let supabaseQuery = supabase
          .from('vendor_peptide_offers')
          .select('*, vendors(name, logo_url, coupon_code, coupon_description, website_url, scraped_coupon_code, scraped_coupon_discount, scraped_coupon_expires_at, scraped_coupon_last_seen, scraped_coupon_source)')
          .range(start, end);

        if (query.trim()) {
          const searchPattern = `%${query.trim()}%`;
          supabaseQuery = supabaseQuery.ilike('canonical_peptide', searchPattern);
        }

        // Apply database sorting (vendor_name_asc and price_asc use client-side sorting)
        const sortConfig = getSortConfig(sort);
        sortConfig.forEach((config, index) => {
          if (index === 0) {
            supabaseQuery = supabaseQuery.order(config.column, {
              ascending: config.ascending,
              nullsFirst: config.nullsFirst ?? true,
            });
          } else {
            supabaseQuery = supabaseQuery.order(config.column, {
              ascending: config.ascending,
              nullsFirst: config.nullsFirst ?? true,
            });
          }
        });

        const { data, error: queryError } = await supabaseQuery;

        if (queryError) throw queryError;

        const transformedProducts: VendorProduct[] = (data || []).map((row: any) => ({
          id: row.id,
          vendor_id: row.vendor_id,
          vendor_name: row.vendors?.name || 'Unknown Vendor',
          canonical_peptide: row.canonical_peptide,
          peptide_name_raw: row.peptide_name_raw,
          product_name: row.product_name,
          product_url: row.product_url,
          price: Number(row.price),
          currency: row.currency || 'USD',
          vial_size_text: row.vial_size_text,
          vial_size_mg: row.vial_size_mg ? Number(row.vial_size_mg) : null,
          price_per_mg: row.price_per_mg ? Number(row.price_per_mg) : null,
          coupon_code: row.vendors?.coupon_code || null,
          coupon_description: row.vendors?.coupon_description || null,
          scraped_coupon_code: row.vendors?.scraped_coupon_code || null,
          scraped_coupon_discount: row.vendors?.scraped_coupon_discount || null,
          vendor_website_url: row.vendors?.website_url || null,
          vendor_logo_url: row.vendors?.logo_url || null,
          affiliate_url: row.affiliate_url || null,
        }));

        setProducts((prev) => (pageNum === 0 ? transformedProducts : [...prev, ...transformedProducts]));
        setHasMore(transformedProducts.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [getSortConfig]);

  // Fetch products when query or sort changes
  useEffect(() => {
    fetchProducts(debouncedQuery, 0, sortOption);
  }, [debouncedQuery, sortOption, fetchProducts]);

  // Load more products
  const loadMore = useCallback(() => {
    if (!loading && !isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(debouncedQuery, nextPage, sortOption);
    }
  }, [loading, isLoadingMore, hasMore, page, debouncedQuery, sortOption, fetchProducts]);

  // Handle product click
  const handleProductClick = useCallback((product: VendorProduct) => {
    const url = product.affiliate_url || product.product_url;
    if (url) {
      // Track click before opening URL (non-blocking)
      const peptideName = product.canonical_peptide || product.peptide_name_raw || null;
      trackVendorClick({
        vendor_id: product.vendor_id,
        offer_id: product.id,
        affiliate_url: url,
        source: 'home_page',
        device_platform: 'web',
        peptide_name: peptideName,
        event_type: 'vendor_click',
      }).catch((err) => {
        // Silently handle tracking errors - don't block user experience
        if (import.meta.env.DEV) {
          console.error('Failed to track vendor click:', err);
        }
      });

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Generate structured data
  const structuredData = useMemo(() => {
    const baseUrl = getBaseUrl();
    const data = [
      generateOrganizationSchema(baseUrl),
      generateWebSiteSchema(baseUrl, `${baseUrl}/?q={search_term_string}`),
    ];

    // Add product schemas for first few products
    products.slice(0, 10).forEach((product) => {
      const peptideName = product.canonical_peptide || product.peptide_name_raw || product.product_name;
      data.push(
        generateProductSchema({
          name: peptideName,
          description: `${peptideName} from ${product.vendor_name}`,
          price: product.price,
          currency: product.currency,
          url: product.affiliate_url || product.product_url,
          vendor: product.vendor_name,
        })
      );
    });

    return data;
  }, [products]);

  const pageTitle = searchQuery.trim()
    ? `Search Results for "${searchQuery}" - Peptide Finder`
    : 'Peptide Finder - Find and Compare Peptides';
  const pageDescription = searchQuery.trim()
    ? `Find ${searchQuery} peptides from trusted vendors. Compare prices, view coupons, and find the best deals.`
    : 'Your shortcut to trusted peptides at the lowest price. Search peptides by name, compare prices, browse vendors, and find research supplies.';

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="peptides, peptide search, research peptides, peptide vendors, peptide prices, BPC-157, Semax, LL-37"
        structuredData={structuredData}
      />
      <div className="home-page">
        <header className="home-page-header">
          <div className="home-page-container">
            <h1 className="home-page-title">Peptide Finder</h1>
            <p className="home-page-subtitle">
              Your shortcut to trusted peptides at the lowest price
            </p>
            <DownloadButtons
              appStoreUrl="https://apps.apple.com/us/app/peptide-library-peptide-log/id6755092737"
              googlePlayUrl="https://play.google.com/store/apps/details?id=com.peptidefinder.app"
              className="home-page-download-buttons"
            />
          </div>
        </header>

        <main className="home-page-main">
          <div className="home-page-container">
            <section className="home-page-search-section" aria-label="Search peptides">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                suggestions={filteredSuggestions.map((s) => s.name)}
                onSuggestionSelect={(suggestion) => setSearchQuery(suggestion)}
                placeholder="Search peptides by name (BPC-157, Semax, LL-37…)"
              />

              <div className="home-page-sort">
                <label htmlFor="sort-select" className="home-page-sort-label">
                  Sort by:
                </label>
                <select
                  id="sort-select"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value as SortOption)}
                  className="home-page-sort-select"
                  aria-label="Sort products"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {!searchQuery.trim() && topPeptides.length > 0 && (
              <section className="home-page-top-peptides" aria-label="Top peptides">
                <h2 className="home-page-top-peptides-title">Popular Searches</h2>
                <div className="home-page-top-peptides-list">
                  {topPeptides.map((peptide) => (
                    <button
                      key={peptide}
                      type="button"
                      onClick={() => setSearchQuery(peptide)}
                      className="home-page-top-peptide-chip"
                      aria-label={`Search for ${peptide}`}
                    >
                      {peptide}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="home-page-results" aria-label="Search results">
              {loading && products.length === 0 ? (
                <div className="home-page-loading" role="status" aria-live="polite">
                  <p>Loading products...</p>
                </div>
              ) : error ? (
                <div className="home-page-error" role="alert">
                  <p>Error: {error}</p>
                </div>
              ) : products.length === 0 ? (
                <div className="home-page-empty">
                  <p>No products found. Try a different search term.</p>
                </div>
              ) : (
                <>
                  <div className="home-page-products">
                    {products.map((product) => (
                      <PeptideCard
                        key={product.id}
                        product={product}
                        onPress={() => handleProductClick(product)}
                      />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="home-page-load-more">
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={isLoadingMore}
                        className="home-page-load-more-button"
                        aria-label="Load more products"
                      >
                        {isLoadingMore ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </>
  );
}
